import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { memberService } from '../../services/memberService'
import { settingsService } from '../../services/settingsService'
import { showSuccess, showError } from '../../utils/alert'

function MemberForm() {
  const { nba } = useParams()
  const navigate = useNavigate()
  const isEdit = !!nba && !window.location.pathname.includes('/new')

  const [formData, setFormData] = useState({
    nba: '',
    uuid: '',
    nama: '',
    tglMasuk: new Date().toISOString().split('T')[0],
    alamat: '',
    kodeCabang: '',
    kodeLingkungan: '',
    tglLahir: '',
    tempatLahir: '',
    jenisKelamin: '',
    agama: '',
    statusPerkawinan: '',
    pendidikan: '',
    pekerjaan: '',
    tanggungan: 0,
    mobileNo: '',
    namaIbuKandung: '',
    jenisIDCard: 'KTP',
    nomorIDCard: '',
    alamatIDCard: '',
    ahliWaris1: '',
    hubunganWaris1: '',
    ahliWaris2: '',
    hubunganWaris2: '',
    nomorRekening: '',
    jenisBank: '',
    foto: null
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [lingkungan, setLingkungan] = useState([])

  useEffect(() => {
    fetchLingkungan()
    if (isEdit) {
      fetchMember()
    }
  }, [nba])

  const fetchLingkungan = async () => {
    try {
      const response = await settingsService.getLingkungan()
      setLingkungan(response.data.data || [])
    } catch (error) {
      console.error('Error fetching lingkungan:', error)
    }
  }

  const fetchMember = async () => {
    try {
      setLoading(true)
      const response = await memberService.getById(nba)
      const data = response.data.data
      setFormData({
        nba: data.NBA,
        uuid: data.UUID || '',
        nama: data.Nama,
        tglMasuk: data.TglMasuk,
        alamat: data.Alamat || '',
        kodeCabang: data.KodeCabang || '01',
        kodeLingkungan: data.KodeLingkungan || '',
        tglLahir: data.TglLahir || '',
        tempatLahir: data.TempatLahir || '',
        jenisKelamin: data.JenisKelamin || '',
        agama: data.Agama || '',
        statusPerkawinan: data.StatusPerkawinan || '',
        pendidikan: data.Pendidikan || '',
        pekerjaan: data.Pekerjaan || '',
        tanggungan: data.Tanggungan || 0,
        mobileNo: data.MobileNo || '',
        namaIbuKandung: data.NamaIbuKandung || '',
        jenisIDCard: data.JenisIDCard || 'KTP',
        nomorIDCard: data.NomorIDCard || '',
        alamatIDCard: data.AlamatIDCard || '',
        ahliWaris1: data.AhliWaris1 || '',
        hubunganWaris1: data.HubunganWaris1 || '',
        ahliWaris2: data.AhliWaris2 || '',
        hubunganWaris2: data.HubunganWaris2 || '',
        nomorRekening: data.NomorRekening || '',
        jenisBank: data.JenisBank || '',
        foto: data.Photo || null
      })
    } catch (error) {
      console.error('Error fetching member:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      if (isEdit) {
        // Handle foto in update
        let updateData = formData
        if (formData.foto && typeof formData.foto === 'object') {
          const reader = new FileReader()
          const fotoBase64 = await new Promise((resolve) => {
            reader.onload = (e) => resolve(e.target.result)
            reader.readAsDataURL(formData.foto)
          })
          updateData = { ...formData, foto: fotoBase64 }
        }
        await memberService.update(nba, updateData)
        showSuccess('Data anggota berhasil diperbarui')
      } else {
        // Remove kodeCabang and foto from data - backend will use user cabangId
        const { kodeCabang: _, foto: __, ...createData } = formData

        // Convert foto to base64 if exists
        let submitData = createData
        if (formData.foto) {
          const reader = new FileReader()
          const fotoBase64 = await new Promise((resolve) => {
            reader.onload = (e) => resolve(e.target.result)
            reader.readAsDataURL(formData.foto)
          })
          submitData = { ...createData, foto: fotoBase64 }
        }

        const response = await memberService.create(submitData)
        const newNBA = response.data?.data?.nba || formData.nba
        showSuccess('Anggota berhasil didaftarkan', `NBA: ${newNBA}`)
      }
      navigate('/members')
    } catch (error) {
      showError('Gagal menyimpan data', error.response?.data?.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x"></i>
        <p className="mt-2">Memuat data...</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          {isEdit ? 'Edit Data Anggota' : 'Tambah Anggota Baru'}
        </h3>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>NBA (Nomor Buku Anggota) {!isEdit && '*'}</label>
                {isEdit ? (
                  <input
                    type="text"
                    name="nba"
                    className="form-control"
                    value={formData.nba}
                    disabled
                    readOnly
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      name="nba"
                      className="form-control"
                      value={formData.nba}
                      onChange={handleChange}
                      placeholder="Contoh: 01.01.00001 atau 01.01-00001"
                      pattern="[0-9.\-]+"
                      title="NBA hanya boleh berisi angka, titik, dan tanda minus"
                    />
                    <small className="text-muted">
                      <i className="fas fa-info-circle mr-1"></i>
                      NBA hanya boleh berisi angka, titik, dan tanda minus.<br/>
                      Contoh: <code>01.01.00001</code> atau <code>01.01-00001</code>
                    </small>
                  </>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Nama Lengkap *</label>
                <input
                  type="text"
                  name="nama"
                  className="form-control"
                  value={formData.nama}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>UUID</label>
                <input
                  type="text"
                  name="uuid"
                  className="form-control"
                  value={formData.uuid}
                  onChange={handleChange}
                  placeholder="For external application correlation"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Tanggal Masuk *</label>
                <input
                  type="date"
                  name="tglMasuk"
                  className="form-control"
                  value={formData.tglMasuk}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Tempat Lahir</label>
                <input
                  type="text"
                  name="tempatLahir"
                  className="form-control"
                  value={formData.tempatLahir}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Tanggal Lahir</label>
                <input
                  type="date"
                  name="tglLahir"
                  className="form-control"
                  value={formData.tglLahir}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <div className="form-group">
                <label>Jenis Kelamin</label>
                <select
                  name="jenisKelamin"
                  className="form-control"
                  value={formData.jenisKelamin}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="L">Laki-laki</option>
                  <option value="P">Perempuan</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Agama</label>
                <select
                  name="agama"
                  className="form-control"
                  value={formData.agama}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="Islam">Islam</option>
                  <option value="Kristen">Kristen</option>
                  <option value="Katolik">Katolik</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Buddha">Buddha</option>
                  <option value="Konghucu">Konghucu</option>
                  <option value="Penganut Kepercayaan">Penganut Kepercayaan</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Pendidikan</label>
                <select
                  name="pendidikan"
                  className="form-control"
                  value={formData.pendidikan}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="SD">SD</option>
                  <option value="SMP">SMP</option>
                  <option value="SMA">SMA</option>
                  <option value="D3">D3</option>
                  <option value="S1">S1</option>
                  <option value="S2">S2</option>
                  <option value="S3">S3</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Pekerjaan</label>
                <input
                  type="text"
                  name="pekerjaan"
                  className="form-control"
                  value={formData.pekerjaan}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Alamat</label>
                <textarea
                  name="alamat"
                  className="form-control"
                  rows="3"
                  value={formData.alamat}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>No. HP</label>
                <input
                  type="text"
                  name="mobileNo"
                  className="form-control"
                  value={formData.mobileNo}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="form-group">
                <label>Kode Lingkungan *</label>
                <select
                  name="kodeLingkungan"
                  className="form-control"
                  value={formData.kodeLingkungan}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Pilih Lingkungan --</option>
                  {lingkungan && Array.isArray(lingkungan) && lingkungan.map(l => (
                    <option key={l.KodeLingkungan} value={l.KodeLingkungan}>
                      {l.KodeLingkungan} - {l.NamaLingkungan}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  Lingkungan akan menentukan area dalam NBA (Cabang otomatis dari profil koperasi)
                </small>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="form-group">
                <label>Nama Ibu Kandung</label>
                <input
                  type="text"
                  name="namaIbuKandung"
                  className="form-control"
                  value={formData.namaIbuKandung}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>Status Perkawinan</label>
                <select
                  name="statusPerkawinan"
                  className="form-control"
                  value={formData.statusPerkawinan}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="Belum Menikah">Belum Menikah</option>
                  <option value="Menikah">Menikah</option>
                  <option value="Janda/Duda">Janda/Duda</option>
                  <option value="Cerai Hidup">Cerai Hidup</option>
                  <option value="Cerai Mati">Cerai Mati</option>
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Jumlah Tanggungan</label>
                <input
                  type="number"
                  name="tanggungan"
                  className="form-control"
                  value={formData.tanggungan}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Foto</label>
                <input
                  type="file"
                  name="foto"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setFormData({...formData, foto: file});
                    }
                  }}
                />
                {formData.foto && typeof formData.foto === 'string' && (
                  <div className="mt-2">
                    <img
                      src={formData.foto}
                      alt="Foto Anggota"
                      style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                    />
                  </div>
                )}
                {formData.foto && typeof formData.foto === 'object' && (
                  <div className="mt-2">
                    <img
                      src={URL.createObjectURL(formData.foto)}
                      alt="Preview Foto"
                      style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>Jenis Kartu Identitas</label>
                <select
                  name="jenisIDCard"
                  className="form-control"
                  value={formData.jenisIDCard}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="KTP">KTP</option>
                  <option value="SIM">SIM</option>
                  <option value="PASPOR">PASPOR</option>
                  <option value="KK">Kartu Keluarga</option>
                  <option value="KITAS">KITAS</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Nomor Kartu Identitas</label>
                <input
                  type="text"
                  name="nomorIDCard"
                  className="form-control"
                  value={formData.nomorIDCard}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Alamat Sesuai Kartu Identitas</label>
                <textarea
                  name="alamatIDCard"
                  className="form-control"
                  rows="2"
                  value={formData.alamatIDCard}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Ahli Waris 1</label>
                <input
                  type="text"
                  name="ahliWaris1"
                  className="form-control"
                  value={formData.ahliWaris1}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Hubungan</label>
                <select
                  name="hubunganWaris1"
                  className="form-control"
                  value={formData.hubunganWaris1}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="Suami">Suami</option>
                  <option value="Istri">Istri</option>
                  <option value="Anak">Anak</option>
                  <option value="Ayah">Ayah</option>
                  <option value="Ibu">Ibu</option>
                  <option value="Saudara">Saudara</option>
                  <option value="Kakek">Kakek</option>
                  <option value="Nenek">Nenek</option>
                  <option value="Paman">Paman</option>
                  <option value="Bibi">Bibi</option>
                  <option value="Sepupu">Sepupu</option>
                  <option value="Keponakan">Keponakan</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Ahli Waris 2</label>
                <input
                  type="text"
                  name="ahliWaris2"
                  className="form-control"
                  value={formData.ahliWaris2}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Hubungan</label>
                <select
                  name="hubunganWaris2"
                  className="form-control"
                  value={formData.hubunganWaris2}
                  onChange={handleChange}
                >
                  <option value="">Pilih</option>
                  <option value="Suami">Suami</option>
                  <option value="Istri">Istri</option>
                  <option value="Anak">Anak</option>
                  <option value="Ayah">Ayah</option>
                  <option value="Ibu">Ibu</option>
                  <option value="Saudara">Saudara</option>
                  <option value="Kakek">Kakek</option>
                  <option value="Nenek">Nenek</option>
                  <option value="Paman">Paman</option>
                  <option value="Bibi">Bibi</option>
                  <option value="Sepupu">Sepupu</option>
                  <option value="Keponakan">Keponakan</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Nomor Rekening</label>
                <input
                  type="text"
                  name="nomorRekening"
                  className="form-control"
                  value={formData.nomorRekening}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Jenis Bank</label>
                <input
                  type="text"
                  name="jenisBank"
                  className="form-control"
                  value={formData.jenisBank}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Menyimpan...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                Simpan
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-default ml-2"
            onClick={() => navigate('/members')}
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  )
}

export default MemberForm
